import { SupportingItemsPorts } from 'admin/ports'
import { AlertFactory } from 'alerts/Alert'
import DocumentType from 'documents/DocumentType'
import { DeferredPromise } from 'infra/deferred'
import ModalViewModel from 'infra/ModalViewModel'
import Checklist, { DocumentSupportingItemInput } from 'jobs/Checklist'
import DocumentRequestStore from 'jobs/document-requests/DocumentRequestStore'
import { action, computed, makeObservable, observable, override } from 'mobx'
import { task } from 'mobx-task'
import { Id, Nullable } from 'types/definitions'

export class DocumentRequestDialogViewModel extends ModalViewModel {
  @observable
  selectedDocumentTypes: DocumentType[] = []

  /**
   * Defers the resolution of the dialog.
   */
  deferredResolution = new DeferredPromise<DocumentType[]>()

  constructor(
    private jobId: Id,
    private supportingItemsAdapter: SupportingItemsPorts,
    private documentRequestStore: DocumentRequestStore,
    private newAlert: AlertFactory,
    private getChecklist: () => Checklist | undefined,
  ) {
    super()
    makeObservable(this)
    this.show$.subscribe(this.activate)
  }

  @computed
  get documentTypes() {
    return this.supportingItemsAdapter.documentTypes
  }

  @computed
  get checklist() {
    return this.getChecklist()
  }

  /**
   * Prompts the user to create a document request.
   *
   * @returns
   */
  @action
  promptForDocumentRequest(): Promise<Nullable<DocumentType[]>> {
    this.show()
    this.deferredResolution.reset()

    if (this.checklist) {
      this.selectedDocumentTypes = this.checklist.sections.items.flatMap((s) =>
        s.requestedSupportingItems.items
          .filter(
            (i) => i.adHoc && i.input instanceof DocumentSupportingItemInput,
          )
          .map((i) => i.input as DocumentSupportingItemInput)
          .filter((input) => input?.documentType !== undefined)
          .map((input) => input.documentType),
      )
    }

    return this.deferredResolution.promise ?? Promise.resolve(null)
  }

  /**
   * Toggles a document type's selection.
   *
   * @param documentType
   */
  @action
  toggle(documentType: DocumentType) {
    if (this.selectedDocumentTypes.includes(documentType)) {
      this.selectedDocumentTypes = this.selectedDocumentTypes.filter(
        (dt) => dt !== documentType,
      )
    } else {
      this.selectedDocumentTypes = [...this.selectedDocumentTypes, documentType]
    }
  }

  activate = task.resolved(async () => {
    await this.supportingItemsAdapter.fetchDocumentTypes()
  })

  submit = task.resolved(async () => {
    try {
      await this.documentRequestStore.submitDocumentRequest(
        this.jobId,
        this.selectedDocumentTypes,
      )

      this.deferredResolution.resolve(this.selectedDocumentTypes)
      this.close()
    } catch (error) {
      this.newAlert().fromError(error).show()
      console.error(error)
    }
  })

  /**
   * @inheritDoc
   */
  @override
  async close() {
    this.deferredResolution.resolve(null)
    super.close()
  }
}
